import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { NavigationEnd } from '@angular/router'
import { combineLatest, Observable, Subscription } from 'rxjs'
import { filter, map, startWith } from 'rxjs/operators'
import {
  NavMenuItem,
  NavMenuSection
} from '../../providers/nav-builder/nav-builder-types'
import { BaseNavComponent } from '../base-nav/base-nav.component'

@Component({
  selector: 'vdr-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss']
})
export class MainNavComponent extends BaseNavComponent implements OnInit {
  @Input() displayMode: string | undefined
  @Output() itemClick = new EventEmitter<NavMenuItem>()
  mainMenuConfig$: Observable<NavMenuSection[]>
  expandedSections: string[] = []
  activeItem: NavMenuItem | null = null
  private activeItemSubscription?: Subscription

  override ngOnInit(): void {
    super.ngOnInit()

    this.mainMenuConfig$ = this.navBuilderService.menuConfig$.pipe(
      map((sections) =>
        sections.filter((s) =>
          this.displayMode ? s.displayMode === this.displayMode : !s.displayMode
        )
      )
    )
    const url$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.router.url)
    )
    this.activeItemSubscription = combineLatest([
      this.mainMenuConfig$,
      url$
    ]).subscribe(([sections, url]) => {
      this.activeItem = this.findActiveItem(sections, url)
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.activeItemSubscription?.unsubscribe()
  }

  /**
   * The item whose link is the longest match of the URL, so a page nested under
   * another item's link (/hotel/calendar under /hotel) highlights only its own item.
   */
  private findActiveItem(
    sections: NavMenuSection[],
    url: string
  ): NavMenuItem | null {
    const path = url.split(/[?#;]/)[0]
    let best: { item: NavMenuItem; length: number } | null = null
    for (const item of sections.flatMap((section) => section.items)) {
      const link = this.itemPath(item)
      if (link === null) continue
      const matches =
        link === '/'
          ? path === '/'
          : path === link || path.startsWith(`${link}/`)
      if (matches && (!best || link.length > best.length)) {
        best = { item, length: link.length }
      }
    }
    return best?.item ?? null
  }

  private itemPath(item: NavMenuItem): string | null {
    const link = this.getRouterLink(item)
    if (!link) return null
    const commands = Array.isArray(link) ? link : [link]
    return this.router.serializeUrl(this.router.createUrlTree(commands))
  }

  toggleExpand(section: NavMenuSection) {
    if (this.expandedSections.includes(section.id)) {
      this.expandedSections = this.expandedSections.filter(
        (id) => id !== section.id
      )
    } else {
      this.expandedSections.push(section.id)
    }
  }

  setExpanded(section: NavMenuSection, expanded: boolean) {
    if (expanded) {
      this.expandedSections.push(section.id)
    } else {
      this.expandedSections = this.expandedSections.filter(
        (id) => id !== section.id
      )
    }
  }

  getStyleForSection(section: NavMenuSection) {
    if (section.collapsible) {
      if (this.expandedSections.includes(section.id)) {
        return {
          opacity: 1,
          visibility: 'visible'
        }
      } else {
        return { maxHeight: '0px', opacity: 0, visibility: 'hidden' }
      }
    }
  }

  isDashboardSection(section: NavMenuSection): boolean {
    return section.id === 'dashboard'
  }

  onItemClick(item: NavMenuItem, event: MouseEvent) {
    item.onClick?.(event)
    this.itemClick.emit(item)
  }
}
